<template>
  <div class="page-parameters">

    <!-- Nav menu -->
    <layout-nav-side-menu
      v-model="navSection"
      :items="navSectionOptions"
      class="page-parameters__menu"
      @update:model-value="onRouteChangeIntent"
    >
      <template #[`item(${NAV_SECTION.CALENDAR})`]="{ item, status }">
        <ui-tooltip-wrapper
          :status="status !== 'enabled' ?
            UI.STATUS.ENABLED :
            UI.STATUS.DISABLED
          "
          v-bind="{
            title: `Vous n'avez pas les droits afin d'effectuer cette action`,
            variant: UI.VARIANT.LIGHT,
            placement: 'bottom',
          }"
        >
          <ui-ripple
            :variant="UI.VARIANT.DARK"
            :status="status"
          >
            {{ item.text }}
          </ui-ripple>
        </ui-tooltip-wrapper>
      </template>
        <template #[`item(${NAV_SECTION.MISC_LEAVE})`]="{ item, status }">
        <ui-tooltip-wrapper
          :status="status !== 'enabled' ?
            UI.STATUS.ENABLED :
            UI.STATUS.DISABLED
          "
          v-bind="{
            title: `Vous n'avez pas les droits afin d'effectuer cette action`,
            variant: UI.VARIANT.LIGHT,
            placement: 'bottom',
          }"
        >
          <ui-ripple
            :variant="UI.VARIANT.DARK"
            :status="status"
          >
            {{ item.text }}
          </ui-ripple>
        </ui-tooltip-wrapper>
      </template>
    </layout-nav-side-menu>

    <div class="page-parameters__body">
      <nuxt-page />
    </div>

  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables

$side-menu-width: 188px

.page-parameters
  flex-grow: 1
  display: flex
  height: 100%
  background-color: $background-secondary

  &__body
    flex-grow: 1
    height: 100%
    display: flex
    flex-direction: column

  &__menu
    flex-shrink: 0
    background-color: $background-tertiary
    padding: $layout-spacer-y ($layout-spacer-x * .5)

  :deep(.page-parameters__tab--sheet),
  :deep(.page-parameters__tab--misc),
  :deep(.page-parameters__tab--calendar)
    flex-grow: 1

    padding-left: $layout-spacer-x
    padding-right: $layout-spacer-x
    padding-top: $layout-spacer-y
    padding-bottom: $layout-spacer-y

  :deep(.page-parameters__tab--calendar)
    width: map-get($breakpoints, 'tablet')
    padding-top: $layout-spacer-y * .5

</style>

<script lang="ts">
import { not, propOr, test } from '~/utils/lib/core/prelude'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { getMiscLeaveRoute, getCpRttRoute, getCalendarRoute } from '~/stores/transat/parameters'
import type { LayoutNavTabMenuPropsItem } from '~/components/layout/nav/TabMenu.vue'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { extractRouteCompanyCode } from '~/stores/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import { ACLS } from '@ghsfr/acls-commons'
import { useStore_transat_layout } from '~/stores/transat/layout'

export type NAV_SECTION = typeof NAV_SECTION[keyof typeof NAV_SECTION]
export const NAV_SECTION = {
  CP_RTT: 'cp_rtt',
  CALENDAR: 'calendar',
  MISC_LEAVE: 'misc_leave',
} as const

export default defineNuxtComponent({
  provide() {
    return {
      setNavSection: (navSection: NAV_SECTION) => this.navSection = navSection
    }
  },
  setup() {
    const navigationContext = useCompanyRequiredNavigationContext()
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.ABSENCES,
      middleware: function(to) {
        const needDefaultRedirection = test(
          /parameters$/,
          to.path
        )

        if (not(needDefaultRedirection))
          return

        const structure_id :string = propOr('', 'dataId', to.params)
        const company_code :string = extractRouteCompanyCode(to)

        const defaultRoute = getCpRttRoute(
          structure_id,
          company_code
        )

        return navigateTo(defaultRoute, { replace: true })
      }
    })
    useHead({
      title: `Paramètres - ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
    })

    const store_transat_layout = useStore_transat_layout()

    store_transat_layout.setNavSection('parameters')

    return {
      ...navigationContext,
      ...useAccessControl(),
    }
  },
  data() {
    const navSectionOptions = [
      {
        id: NAV_SECTION.CP_RTT,
        text: `CP / RTT`,
        status: 'enabled'
      },
      {
        id: NAV_SECTION.CALENDAR,
        text: `Calendrier`,
        // @ts-expect-error: "data" hook not injecting "setup" dependencies
        status: this.hasACL(ACLS.LEAVE_PUBLIC_HOLIDAY_GET) ?
          'enabled' :
          'disabled'
      },
      {
        id: NAV_SECTION.MISC_LEAVE,
        text: `Autres absences`,
        // @ts-expect-error: "data" hook not injecting "setup" dependencies
        status: this.hasACL(ACLS.LEAVE_RULE_GET) ?
          'enabled' :
          'disabled'
      },
    ] satisfies LayoutNavTabMenuPropsItem[]

    return {
      NAV_SECTION,
      navSectionOptions,
      navSection: (NAV_SECTION.CP_RTT as NAV_SECTION),
    }
  },
  methods: {
    onRouteChangeIntent(nav_section) {
      const {
        structure: { id },
        company: { code_societe },
      } = this.fromCompanyLevel()

      const route = (() => {
        switch (nav_section as NAV_SECTION) {
          case NAV_SECTION.CP_RTT: return getCpRttRoute(
              id,
              code_societe
            )
          case NAV_SECTION.MISC_LEAVE: return getMiscLeaveRoute(
            id,
            code_societe
          )
          case NAV_SECTION.CALENDAR: return getCalendarRoute(
            id,
            code_societe
          )
        }
      })()

      return navigateTo(route)
    },
  },
})
</script>
